import {
  Avatar,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  Collapse,
  Divider,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
} from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../../../contexts/user-context/user-context';
import { useNavigate } from 'react-router-dom';
import LogoutIcon from '@mui/icons-material/Logout';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { RoleChips } from '../../common/role-chips';
import { UserType } from '../../../enums/users';
import { secondaryPaletteColor } from '../../../configurations/global.config';
import { user_is_admin } from '../../../utils/permissions';
import { useSelectedBranch } from '../../../contexts/selected-branch-context';

function UserProfileCompact() {
  const { user, setUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const {
    selectedBranch,
    availableBranches,
    refreshAvailableBranches,
    setSelectedBranch,
    isLoading: isLoadingBranches,
    canSelectBranch,
  } = useSelectedBranch();

  useEffect(()=>{refreshAvailableBranches()}, [])

  if (!user) return null;

  const isEmployee = user.type === UserType.EMPLOYEE;
  const isOrgOrEmployee = user.type === UserType.ORGANISATION || isEmployee;
  const displayRole =
    user.is_admin
      ? 'Admin'
      : isEmployee
      ? 'Employee'
      : user.type.toString();

  return (
    <Box
      sx={{
        background: secondaryPaletteColor,
        p: 1,
        borderRadius: 2,
        mx: 1,
        mt: 1,
        mb: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Avatar sx={{ width: 32, height: 32 }}>
          {user.name?.[0]?.toUpperCase()}
        </Avatar>

        <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
          <Typography variant="body2" noWrap fontWeight={600}>
            {user.name}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
          >
            {displayRole}
          </Typography>
        </Box>

        <Tooltip title="Logout">
          <IconButton
            size="small"
            onClick={() => {
              setUser(null);
              navigate('login');
            }}
          >
            <LogoutIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <IconButton size="small" onClick={() => setExpanded(!expanded)}>
          {expanded ? (
            <ExpandLessIcon fontSize="small" />
          ) : (
            <ExpandMoreIcon fontSize="small" />
          )}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Divider sx={{ my: 1 }} />
        <Box sx={{ pl: 0.5 }}>
          <Typography variant="caption" display="block">
            Org ID: {user.organisation?.org_id ?? '-'}
          </Typography>
          <Typography variant="caption" display="block">
            Phone: {user.phone_number ?? '-'}
          </Typography>

          {canSelectBranch && (
            <Box sx={{ mt: 1 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="branch-select-label">Branch</InputLabel>
                <Select
                  labelId="branch-select-label"
                  id="branch-select"
                  value={selectedBranch?.id ?? ''}
                  label="Branch"
                  onChange={(e) => {
                    const branch = availableBranches.find(
                      (b) => String(b.id) === String(e.target.value)
                    );
                    if (branch) {
                      setSelectedBranch(branch);
                    }
                  }}
                  disabled={isLoadingBranches || availableBranches.length === 0}
                  sx={{ fontSize: '0.75rem' }}
                >
                  {isLoadingBranches ? (
                    <MenuItem disabled>
                      <CircularProgress size={16} sx={{ mr: 1 }} />
                      Loading branches...
                    </MenuItem>
                  ) : availableBranches.length === 0 ? (
                    <MenuItem disabled>No branches available</MenuItem>
                  ) : (
                    availableBranches.map((branch) => (
                      <MenuItem key={branch.id} value={branch.id}>
                        {branch.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Box>
          )}
        </Box>

        {isEmployee && !user.is_admin && (
          <Box mt={1}>
            <RoleChips
              is_admin={user_is_admin(user)}
              centered={false}
              roles={user.roles ?? []}
              allRoles={user.roles ?? []}
              allRolesLoading={false}
            />
          </Box>
            
          )}
      </Collapse>
    </Box>
  );
}

export default UserProfileCompact;
